import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Spinner } from 'reactstrap'
import MaterialReactTable from 'material-react-table'
import { MRT_Localization_PT } from 'material-react-table/locales/pt'
import oikosApi from '../../services/oikos'
import { notifyError, notifySuccess } from '../../utils/notify'
import { getOikosFileUrl } from '../../utils/linksOikos/link'
import './documentosRepositorio.css'

const ativo = (row, key) => {
  const value = row?.[key]
  return value === undefined || value === null || value === '' || Number(value) === 1
}

const formatDate = (value) => {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return ''
  return `${match[3]}/${match[2]}/${match[1]}`
}

const nomeDocumento = (doc, tipo) => {
  const desc = String(doc.documento_desc || '').trim()
  if (desc) return desc
  if (tipo) return tipo
  return `Documento ${doc.documento_id}`
}

const EXT_IMAGEM = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']
const EXT_PAGINA = ['pdf', 'txt']

const extensaoArquivo = (file) => {
  const nome = String(file?.arquivo_desc || file?.arquivo_caminho || '')
  const match = nome.split('?')[0].match(/\.([a-z0-9]+)$/i)
  return match ? match[1].toLowerCase() : ''
}

const versoesDoDocumento = (files) => [...files]
  .sort((a, b) => {
    const da = String(a.arquivo_dtCria || a.arquivo_dtMod || '')
    const db = String(b.arquivo_dtCria || b.arquivo_dtMod || '')
    if (da !== db) return da < db ? -1 : 1
    return Number(a.arquivo_id) - Number(b.arquivo_id)
  })
  .map((file, index, lista) => ({
    ...file,
    versao: index + 1,
    atual: index === lista.length - 1,
  }))
  .reverse()

export default function DocumentosRepositorio({ embedded = false }) {
  const [loading, setLoading] = useState(true)
  const [setores, setSetores] = useState([])
  const [vinculos, setVinculos] = useState([])
  const [documentos, setDocumentos] = useState([])
  const [tipos, setTipos] = useState([])
  const [arquivos, setArquivos] = useState([])
  const [setorId, setSetorId] = useState(null)
  const [enviandoId, setEnviandoId] = useState(null)
  const [visto, setVisto] = useState(null)
  const fileRef = useRef(null)
  const alvoRef = useRef(null)

  useEffect(() => {
    if (!visto) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') setVisto(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [visto])

  useEffect(() => {
    let cancel = false
    const load = async () => {
      setLoading(true)
      try {
        const [setoresRes, vincRes, docsRes, tiposRes, arqRes] = await Promise.all([
          oikosApi.post('/', { action: 'get', table: 'setor', data: { setor_ativo: 1 } }),
          oikosApi.post('/', {
            action: 'get',
            table: 'setorVinc',
            data: { setorVinc_tabRef: 'documento', setorVinc_ativo: 1 },
          }),
          oikosApi.post('/', { action: 'get', table: 'documento', data: { documento_ativo: 1 } }),
          oikosApi.post('/', { action: 'get', table: 'tpDocumento' }),
          oikosApi.post('/', {
            action: 'get',
            table: 'arquivo',
            data: { arquivo_tabRef: 'documento', arquivo_ativo: 1 },
          }),
        ])
        if (cancel) return
        setSetores(Array.isArray(setoresRes.data) ? setoresRes.data : [])
        setVinculos(Array.isArray(vincRes.data) ? vincRes.data : [])
        setDocumentos(Array.isArray(docsRes.data) ? docsRes.data : [])
        setTipos(Array.isArray(tiposRes.data) ? tiposRes.data : [])
        setArquivos(Array.isArray(arqRes.data) ? arqRes.data : [])
      } catch (err) {
        if (!cancel) notifyError('Não foi possível carregar os documentos.')
      } finally {
        if (!cancel) setLoading(false)
      }
    }
    load()
    return () => {
      cancel = true
    }
  }, [])

  const tipoPorId = useMemo(() => {
    const map = {}
    tipos.forEach((tipo) => {
      map[String(tipo.tpDocumento_id)] = tipo.tpDocumento_desc || ''
    })
    return map
  }, [tipos])

  const arquivosPorDoc = useMemo(() => {
    const map = {}
    arquivos.filter((arq) => ativo(arq, 'arquivo_ativo')).forEach((arq) => {
      const id = String(arq.arquivo_tabId)
      if (!map[id]) map[id] = []
      map[id].push(arq)
    })
    return map
  }, [arquivos])

  const docsPorId = useMemo(() => {
    const map = {}
    documentos.filter((doc) => ativo(doc, 'documento_ativo')).forEach((doc) => {
      map[String(doc.documento_id)] = doc
    })
    return map
  }, [documentos])

  const grupos = useMemo(() => {
    const porSetor = {}
    vinculos
      .filter((v) => String(v.setorVinc_tabRef) === 'documento' && ativo(v, 'setorVinc_ativo'))
      .forEach((v) => {
        const doc = docsPorId[String(v.setorVinc_tabId)]
        if (!doc) return
        const id = String(v.setor_id)
        if (!porSetor[id]) porSetor[id] = []
        if (!porSetor[id].some((item) => String(item.documento_id) === String(doc.documento_id))) {
          porSetor[id].push(doc)
        }
      })

    return setores
      .filter((setor) => ativo(setor, 'setor_ativo') && porSetor[String(setor.setor_id)]?.length)
      .map((setor) => ({
        id: String(setor.setor_id),
        nome: setor.setor_nome || `Setor ${setor.setor_id}`,
        documentos: porSetor[String(setor.setor_id)],
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
  }, [setores, vinculos, docsPorId])

  useEffect(() => {
    if (!grupos.length) {
      setSetorId(null)
      return
    }
    if (!grupos.some((grupo) => grupo.id === setorId)) {
      setSetorId(grupos[0].id)
    }
  }, [grupos, setorId])

  const grupo = grupos.find((item) => item.id === setorId) || null

  const rows = useMemo(() => {
    if (!grupo) return []
    return grupo.documentos.map((doc) => {
      const tipo = tipoPorId[String(doc.tpDocumento_id)] || ''
      const files = arquivosPorDoc[String(doc.documento_id)] || []
      return {
        ...doc,
        nome: nomeDocumento(doc, tipo),
        tipo: tipo || '—',
        data: formatDate(doc.documento_dtEmissao || doc.documento_iniVig),
        arquivos: files,
      }
    })
  }, [grupo, tipoPorId, arquivosPorDoc])

  const pedirVersao = (doc) => {
    alvoRef.current = doc
    if (!fileRef.current) return
    fileRef.current.value = ''
    fileRef.current.click()
  }

  const enviarVersao = async (event) => {
    const file = event.target.files?.[0]
    const doc = alvoRef.current
    event.target.value = ''
    if (!file || !doc?.documento_id) return
    setEnviandoId(doc.documento_id)
    try {
      const formData = new FormData()
      formData.append('file', file, file.name)
      formData.append('table', 'arquivo')
      formData.append('action', 'upload_file')
      formData.append('arquivo_tabRef', 'documento')
      formData.append('arquivo_tabId', doc.documento_id)
      formData.append('arquivo_privacidade', doc.privacidade_id || 2)
      formData.append('arquivo_desc', file.name)
      await oikosApi.post('/', formData)
      const arqRes = await oikosApi.post('/', {
        action: 'get',
        table: 'arquivo',
        data: { arquivo_tabRef: 'documento', arquivo_ativo: 1 },
      })
      setArquivos(Array.isArray(arqRes.data) ? arqRes.data : [])
      notifySuccess('Nova versão adicionada.')
    } catch (err) {
      notifyError('Não foi possível adicionar a versão.')
    } finally {
      setEnviandoId(null)
    }
  }

  const columns = useMemo(() => [
    { accessorKey: 'nome', header: 'Nome' },
    { accessorKey: 'tipo', header: 'Tipo' },
    {
      accessorKey: 'data',
      header: 'Data do envio',
      size: 340,
      Cell: ({ row }) => {
        const enviando = String(enviandoId) === String(row.original.documento_id)
        return (
          <div className="docs-repo__date-cell">
            <span>{row.original.data || '—'}</span>
            <button
              type="button"
              className="docs-repo__version"
              disabled={enviando}
              onClick={(event) => {
                event.stopPropagation()
                pedirVersao(row.original)
              }}
            >
              {enviando ? 'Enviando…' : 'Adicionar nova versão'}
            </button>
          </div>
        )
      },
    },
  ], [enviandoId])

  const repo = (
      <div className="docs-repo">
        <aside className="docs-repo__nav" aria-label="Setores disponíveis">
          {loading && <p className="docs-repo__nav-empty">Carregando…</p>}
          {!loading && grupos.length === 0 && (
            <p className="docs-repo__nav-empty">Nenhum setor com documento.</p>
          )}
          {grupos.map((item) => (
            <button
              key={item.id}
              type="button"
              className={item.id === setorId ? 'is-on' : undefined}
              onClick={() => setSetorId(item.id)}
            >
              <span>{item.nome}</span>
              <span className="docs-repo__count">{item.documentos.length}</span>
            </button>
          ))}
        </aside>
        <main className="docs-repo__main">
          <header className="docs-repo__head">
            <h1>Documentos</h1>
            <p>
              {grupo
                ? `${grupo.nome}. Clique na linha para ver o histórico de versões.`
                : 'Repositório dos documentos salvos, por setor disponível.'}
            </p>
          </header>
          {loading && (
            <div className="docs-repo__loading">
              <Spinner size="sm" /> Carregando documentos…
            </div>
          )}
          {!loading && !grupo && (
            <p className="docs-repo__empty">Nenhum documento nos setores disponíveis.</p>
          )}
          <input
            ref={fileRef}
            type="file"
            className="docs-repo__file"
            onChange={enviarVersao}
          />
          {!loading && grupo && (
            <MaterialReactTable
              columns={columns}
              data={rows}
              localization={MRT_Localization_PT}
              getRowId={(row) => String(row.documento_id)}
              enableColumnFilters
              enableGlobalFilter
              enableDensityToggle={false}
              initialState={{ density: 'compact' }}
              renderDetailPanel={({ row }) => {
                const versoes = versoesDoDocumento(row.original.arquivos || [])
                if (!versoes.length) {
                  return <p className="docs-repo__empty mb-0">Nenhuma versão neste documento.</p>
                }
                return (
                  <table className="docs-repo__files">
                    <thead>
                      <tr>
                        <th>Histórico de versões</th>
                        <th>Data</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {versoes.map((file) => (
                        <tr key={file.arquivo_id}>
                          <td>
                            {`Versão ${file.versao}${file.atual ? ' (atual)' : ''}`}
                            {' — '}
                            {file.arquivo_desc || file.arquivo_caminho || 'Arquivo'}
                          </td>
                          <td>{formatDate(file.arquivo_dtCria || file.arquivo_dtMod) || '—'}</td>
                          <td>
                            {file.arquivo_caminho ? (
                              <div className="docs-repo__actions">
                                <button
                                  type="button"
                                  className="docs-repo__view"
                                  onClick={() => {
                                    const ext = extensaoArquivo(file)
                                    setVisto({
                                      url: getOikosFileUrl(file.arquivo_caminho),
                                      nome: file.arquivo_desc || file.arquivo_caminho || 'Documento',
                                      ext,
                                    })
                                  }}
                                >
                                  Visualizar
                                </button>
                                <a
                                  className="docs-repo__download"
                                  href={getOikosFileUrl(file.arquivo_caminho)}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Baixar
                                </a>
                              </div>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              }}
            />
          )}
        </main>
        {visto && createPortal(
          <div
            className="docs-repo__viewer"
            role="presentation"
            onClick={() => setVisto(null)}
          >
            <div
              className="docs-repo__viewer-box"
              role="dialog"
              aria-modal="true"
              aria-label={visto.nome}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="docs-repo__viewer-bar">
                <strong>{visto.nome}</strong>
                <button type="button" className="docs-repo__viewer-close" onClick={() => setVisto(null)}>
                  Fechar
                </button>
              </div>
              {EXT_IMAGEM.includes(visto.ext) && (
                <img className="docs-repo__viewer-image" src={visto.url} alt={visto.nome} />
              )}
              {EXT_PAGINA.includes(visto.ext) && (
                <iframe className="docs-repo__viewer-frame" src={visto.url} title={visto.nome} />
              )}
              {!EXT_IMAGEM.includes(visto.ext) && !EXT_PAGINA.includes(visto.ext) && (
                <p className="docs-repo__viewer-note">
                  Este formato abre pelo botão Baixar.
                </p>
              )}
            </div>
          </div>,
          document.body,
        )}
      </div>
  )

  if (embedded) return repo
  return <div className="page-content">{repo}</div>
}
